import { useEffect, useState, type ReactNode } from "react";
import type { FamilyScheduleItemDto, ViewerVideoListItemDto } from "@madamuh/domain";
import { Link } from "react-router-dom";
import {
  DEFAULT_TENANT_TIMEZONE,
  formatTenantDateTime,
  formatTenantDateTimeRange,
  formatTenantTime,
  formatTenantDateWithWeekday
} from "../../shared/lib/tenantDateTime";
import { GENERIC_TENANT_LABELS, type TenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus, describeTenantEntityType } from "../../shared/lib/uiLabels";
import {
  ChevronRightIcon,
  ClockIcon,
  LocationPinIcon,
  PersonIcon,
  PlayCircleIcon
} from "../../shared/ui/icons/AppIcons";

function hasActiveAbsence(item: FamilyScheduleItemDto): boolean {
  return item.studentAbsences.some((student) => student.isAbsent);
}

function formatStudentNameWithAbsence(item: FamilyScheduleItemDto, studentName: string): string {
  const absence = item.studentAbsences.find((student) => student.studentDisplayName === studentName);
  return absence?.isAbsent ? `${studentName}（欠席）` : studentName;
}

function AbsenceMiniIcon() {
  return (
    <span className="family-schedule-date__absence-icon" aria-label="欠席連絡あり" title="欠席連絡あり">
      欠
    </span>
  );
}

export function formatFamilyDateTime(value: string, timeZone = DEFAULT_TENANT_TIMEZONE): string {
  return formatTenantDateTime(value, timeZone);
}

export function formatFamilyDate(value: string, timeZone = DEFAULT_TENANT_TIMEZONE): string {
  return formatTenantDateWithWeekday(value, timeZone);
}

export function formatFamilyDateRange(startAt: string, endAt: string, timeZone = DEFAULT_TENANT_TIMEZONE): string {
  return formatTenantDateTimeRange(startAt, endAt, timeZone);
}

export function formatFamilyTimeRange(startAt: string, endAt: string, timeZone = DEFAULT_TENANT_TIMEZONE): string {
  const start = formatTenantTime(startAt, timeZone);
  const end = formatTenantTime(endAt, timeZone);

  if (start === "未設定" || end === "未設定") {
    return "未設定";
  }

  return `${start}〜${end}`;
}

export function formatFamilyScheduleDateParts(startAt: string, timeZone = DEFAULT_TENANT_TIMEZONE): {
  monthDay: string;
  weekday: string;
} {
  const date = new Date(startAt);

  if (Number.isNaN(date.getTime())) {
    return {
      monthDay: "--/--",
      weekday: "-"
    };
  }

  const parts = new Intl.DateTimeFormat("ja-JP", {
    timeZone,
    month: "numeric",
    day: "numeric",
    weekday: "short"
  }).formatToParts(date);

  const month = parts.find((part) => part.type === "month")?.value ?? "--";
  const day = parts.find((part) => part.type === "day")?.value ?? "--";
  const weekday = parts.find((part) => part.type === "weekday")?.value ?? "-";

  return {
    monthDay: `${month}/${day}`,
    weekday
  };
}

export function formatVideoDuration(durationSeconds: number | null): string | null {
  if (durationSeconds === null || durationSeconds < 0 || !Number.isFinite(durationSeconds)) {
    return null;
  }

  const totalSeconds = Math.round(durationSeconds);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}:${String(remainingMinutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function buildScheduleFlagBadges(item: FamilyScheduleItemDto, labels: TenantLabels): Array<{ label: string; background: string }> {
  const badges: Array<{ label: string; background: string }> = [];

  if (item.displayFlags.isCancelled) {
    badges.push({ label: "休講", background: "#fff1f1" });
  }

  if (item.displayFlags.isRescheduled) {
    badges.push({ label: "振替", background: "#fff6e6" });
  }

  if (item.displayFlags.isSubstitute) {
    badges.push({ label: "代講", background: "#eef6ff" });
  }

  if (item.displayFlags.hasVideoReplacement) {
    badges.push({ label: "動画代替", background: "#f3ecff" });
  }

  if (item.displayFlags.isStudioTbd) {
    badges.push({ label: `${labels.studio}調整中`, background: "#f6f6f6" });
  }

  if (item.displayFlags.isTeacherTbd) {
    badges.push({ label: `${labels.teacher}調整中`, background: "#f6f6f6" });
  }

  if (item.displayFlags.isChanged && badges.length === 0) {
    badges.push({ label: "変更あり", background: "#fff6e6" });
  }

  return badges;
}

function renderBadgeChips(
  itemKey: string,
  badges: ReadonlyArray<{ label: string; background: string }>,
  className = "family-badge-list"
) {
  if (badges.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {badges.map((badge) => (
        <span
          key={`${itemKey}:${badge.label}`}
          className="family-badge-chip"
          style={{ background: badge.background }}
        >
          {badge.label}
        </span>
      ))}
    </div>
  );
}

export function FamilyScheduleDateBlock({
  startAt,
  timeZone = DEFAULT_TENANT_TIMEZONE,
  compact = false,
  absent = false
}: {
  startAt: string;
  timeZone?: string;
  compact?: boolean;
  absent?: boolean;
}) {
  const { monthDay, weekday } = formatFamilyScheduleDateParts(startAt, timeZone);

  return (
    <time
      dateTime={startAt}
      aria-label={formatFamilyDate(startAt, timeZone)}
      className={`family-schedule-date${compact ? " family-schedule-date--compact" : ""}${absent ? " family-schedule-date--absent" : ""}`}
    >
      <span className="family-schedule-date__month-day">{monthDay}</span>
      <span className="family-schedule-date__weekday">{weekday}</span>
      {absent ? <AbsenceMiniIcon /> : null}
    </time>
  );
}

export function FamilyScheduleItemCard({
  item,
  showStudentBadges = true,
  timeZone = DEFAULT_TENANT_TIMEZONE,
  labels = GENERIC_TENANT_LABELS,
  to
}: {
  item: FamilyScheduleItemDto;
  showStudentBadges?: boolean;
  timeZone?: string;
  labels?: TenantLabels;
  to?: string | null;
}) {
  const badges = buildScheduleFlagBadges(item, labels);
  const content = (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div style={{ display: "grid", gap: 4 }}>
          <strong style={{ fontSize: 18 }}>{item.title}</strong>
          <div className="family-list-card__support">{formatFamilyDateRange(item.startAt, item.endAt, timeZone)}</div>
        </div>
        <span
          style={{
            fontSize: 12,
            padding: "4px 8px",
            borderRadius: 999,
            background: item.itemType === "event" ? "#eef6ff" : "#f2f2f2"
          }}
        >
          {describeTenantEntityType(item.itemType, labels)}
        </span>
      </div>

      {showStudentBadges ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
          {item.studentDisplayNames.map((studentName) => (
            <span
              key={studentName}
              className={`family-student-pill${item.studentAbsences.some((absence) => absence.studentDisplayName === studentName && absence.isAbsent) ? " family-student-pill--absent" : ""}`}
            >
              {formatStudentNameWithAbsence(item, studentName)}
            </span>
          ))}
        </div>
      ) : null}

      {item.locationLabel ? <div className="family-list-card__support">{labels.studio}: {item.locationLabel}</div> : null}
      {item.displayFlags.isStudioTbd && !item.locationLabel ? <div className="family-list-card__support">{labels.studio}: 調整中</div> : null}
      {item.displayFlags.showTeacherNames ? (
        <div className="family-list-card__support">{labels.teacher}: {item.teacherNameSnapshots.join(", ")}</div>
      ) : null}
      {item.displayFlags.isTeacherTbd && !item.displayFlags.showTeacherNames ? (
        <div className="family-list-card__support">{labels.teacher}: 調整中</div>
      ) : null}
      {item.displayFlags.hasPublicNote && item.publicNote ? (
        <div className="family-list-card__support">お知らせ: {item.publicNote}</div>
      ) : null}

      {renderBadgeChips(item.itemId, badges)}
    </>
  );

  return (
    to ? (
      <Link className="family-list-card" style={{ gap: 10 }} to={to}>
        {content}
      </Link>
    ) : (
      <article className="family-list-card" style={{ gap: 10 }}>
        {content}
      </article>
    )
  );
}

export function FamilySectionHeader({
  icon,
  title,
  subtitle,
  action
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="family-section__header">
      <div className="family-section__header-main">
        <span className="family-section__icon">{icon}</span>
        <div>
          <h2 className="family-section__title">{title}</h2>
          {subtitle ? <p className="family-section__subtitle">{subtitle}</p> : null}
        </div>
      </div>
      {action}
    </div>
  );
}

export function FamilyPersonScheduleCard({
  to,
  displayName,
  profilePhoto,
  nextItem,
  emptyText,
  timeZone = DEFAULT_TENANT_TIMEZONE,
  labels = GENERIC_TENANT_LABELS
}: {
  to: string;
  displayName: string;
  profilePhoto?: { downloadUrl: string } | null;
  nextItem: FamilyScheduleItemDto | null;
  emptyText: string;
  timeZone?: string;
  labels?: TenantLabels;
}) {
  return (
    <Link to={to} className="family-list-card">
      <div className="family-list-card__row">
        <PersonAvatar displayName={displayName} downloadUrl={profilePhoto?.downloadUrl ?? null} variant="familyHome" />
        <div className="family-list-card__main">
          <div className="family-list-card__title-row">
            <h3 className="family-list-card__title">{displayName}</h3>
            <span className="family-list-card__chevron">
              <ChevronRightIcon size={20} />
            </span>
          </div>
          {nextItem ? (
            <>
              <div className="family-list-card__meta">
                <span className="family-inline-meta">
                  <ClockIcon size={16} />
                  {formatFamilyDate(nextItem.startAt, timeZone)} {formatFamilyTimeRange(nextItem.startAt, nextItem.endAt, timeZone)}
                </span>
              </div>
              <div className="family-list-card__support">{nextItem.title}</div>
              <div className="family-list-card__meta">
                <span className="family-inline-meta">
                  <LocationPinIcon size={16} />
                  {nextItem.locationLabel ?? `${labels.studio}未定`}
                </span>
              </div>
            </>
          ) : (
            <div className="family-list-card__support">{emptyText}</div>
          )}
        </div>
      </div>
    </Link>
  );
}

export function PersonAvatar({
  displayName,
  downloadUrl,
  large = false,
  variant = "default"
}: {
  displayName: string;
  downloadUrl: string | null;
  large?: boolean;
  variant?: "default" | "familyHome";
}) {
  const [failed, setFailed] = useState(false);
  const className = [
    "family-list-card__icon",
    large ? "family-list-card__icon--large" : "",
    variant === "familyHome" ? "family-list-card__icon--family-home" : ""
  ].filter(Boolean).join(" ");

  useEffect(() => {
    setFailed(false);
  }, [downloadUrl]);

  return (
    <span className={className}>
      {downloadUrl && !failed ? (
        <img
          className="family-person-photo"
          src={downloadUrl}
          alt={`${displayName}の写真`}
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        <PersonIcon size={large ? 44 : 24} />
      )}
    </span>
  );
}

export function FamilySelfNextScheduleCard({
  to,
  displayName,
  nextItem,
  emptyText,
  showStudentName = false,
  timeZone = DEFAULT_TENANT_TIMEZONE,
  labels = GENERIC_TENANT_LABELS
}: {
  to: string;
  displayName: string;
  nextItem: FamilyScheduleItemDto | null;
  emptyText: string;
  showStudentName?: boolean;
  timeZone?: string;
  labels?: TenantLabels;
}) {
  const badges = nextItem ? buildScheduleFlagBadges(nextItem, labels) : [];

  return (
    <Link to={to} className="family-list-card family-self-next-card">
      {nextItem ? (
        <div className="family-self-next-card__row">
          <FamilyScheduleDateBlock startAt={nextItem.startAt} timeZone={timeZone} absent={hasActiveAbsence(nextItem)} />
          <div className="family-self-next-card__main">
            <div className="family-self-next-card__title-row">
              <div className="family-self-next-card__title-group">
                {showStudentName ? <div className="family-list-card__eyebrow">{displayName}</div> : null}
                <h3 className="family-list-card__title family-break-anywhere">{nextItem.title}</h3>
              </div>
              <span className="family-list-card__chevron">
                <ChevronRightIcon size={18} />
              </span>
            </div>
            <div className="family-list-card__meta">
              <span className="family-inline-meta">
                <ClockIcon size={16} />
                {formatFamilyTimeRange(nextItem.startAt, nextItem.endAt, timeZone)}
              </span>
              <span className="family-inline-meta">
                <LocationPinIcon size={16} />
                {nextItem.locationLabel ?? `${labels.studio}未定`}
              </span>
            </div>
            {nextItem.displayFlags.hasPublicNote && nextItem.publicNote ? (
              <div className="family-list-card__support family-break-anywhere">お知らせ: {nextItem.publicNote}</div>
            ) : null}
            {renderBadgeChips(nextItem.itemId, badges, "family-badge-list family-badge-list--compact")}
          </div>
        </div>
      ) : (
        <div className="family-self-next-card__empty">
          {showStudentName ? <div className="family-list-card__eyebrow">{displayName}</div> : null}
          <div className="family-list-card__title-row">
            <h3 className="family-list-card__title">{labels.student}本人の予定</h3>
            <span className="family-list-card__chevron">
              <ChevronRightIcon size={18} />
            </span>
          </div>
          <div className="family-list-card__support">{emptyText}</div>
        </div>
      )}
    </Link>
  );
}

export function FamilyHomeScheduleRow({
  item,
  timeZone = DEFAULT_TENANT_TIMEZONE,
  labels = GENERIC_TENANT_LABELS,
  to
}: {
  item: FamilyScheduleItemDto;
  timeZone?: string;
  labels?: TenantLabels;
  to?: string | null;
}) {
  const badges = buildScheduleFlagBadges(item, labels);
  const content = (
    <div className="family-home-schedule-row">
      <FamilyScheduleDateBlock startAt={item.startAt} timeZone={timeZone} compact absent={hasActiveAbsence(item)} />
      <div className="family-home-schedule-row__main">
        <div className="family-home-schedule-row__title-row">
          <h3 className="family-list-card__title family-break-anywhere">{item.title}</h3>
          {item.itemType === "event" ? <span className="family-type-pill">{describeTenantEntityType(item.itemType, labels)}</span> : null}
        </div>
        <div className="family-list-card__meta">
          <span className="family-inline-meta">
            <ClockIcon size={16} />
            {formatFamilyTimeRange(item.startAt, item.endAt, timeZone)}
          </span>
          <span className="family-inline-meta">
            <LocationPinIcon size={16} />
            {item.locationLabel ?? `${labels.studio}未定`}
          </span>
        </div>
        {item.studentDisplayNames.length > 0 ? (
          <div className="family-list-card__meta family-list-card__meta--tight">
            {item.studentDisplayNames.map((studentName) => (
              <span
                key={`${item.itemId}:${studentName}`}
                className={`family-student-pill${item.studentAbsences.some((absence) => absence.studentDisplayName === studentName && absence.isAbsent) ? " family-student-pill--absent" : ""}`}
              >
                {formatStudentNameWithAbsence(item, studentName)}
              </span>
            ))}
          </div>
        ) : null}
        {item.displayFlags.showTeacherNames ? (
          <div className="family-list-card__support family-break-anywhere">{labels.teacher}: {item.teacherNameSnapshots.join(", ")}</div>
        ) : null}
        {item.displayFlags.isTeacherTbd && !item.displayFlags.showTeacherNames ? (
          <div className="family-list-card__support">{labels.teacher}: 調整中</div>
        ) : null}
        {item.displayFlags.hasPublicNote && item.publicNote ? (
          <div className="family-list-card__support family-break-anywhere">お知らせ: {item.publicNote}</div>
        ) : null}
        {renderBadgeChips(item.itemId, badges, "family-badge-list family-badge-list--compact")}
      </div>
    </div>
  );

  return (
    to ? (
      <Link className="family-list-card family-home-schedule-card" to={to}>
        {content}
      </Link>
    ) : (
      <article className="family-list-card family-home-schedule-card">
        {content}
      </article>
    )
  );
}

export function FamilyVideoPreviewCard({
  tenantSlug,
  video,
  timeZone = DEFAULT_TENANT_TIMEZONE
}: {
  tenantSlug: string;
  video: ViewerVideoListItemDto;
  timeZone?: string;
}) {
  const duration = formatVideoDuration(video.durationSeconds);
  const updatedLabel = video.publishFrom ? formatFamilyDate(video.publishFrom, timeZone) : null;

  return (
    <Link to={`/t/${tenantSlug}/videos/${video.videoId}`} className="family-video-card">
      <div className="family-video-card__visual">
        <PlayCircleIcon size={42} />
        {duration ? <span className="family-video-card__duration">{duration}</span> : null}
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        <h3 className="family-video-card__title">{video.title ?? "タイトル未設定"}</h3>
        <div className="family-video-card__meta">{video.targetLabel}</div>
        {updatedLabel ? <div className="family-video-card__meta">{updatedLabel} 公開</div> : null}
      </div>
    </Link>
  );
}
